import React, { Fragment, useEffect, useState, useMemo } from "react";
import { Box, Button, Divider, Tooltip, Typography } from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { AMADEUS } from "@capone/common";
import { CallState, IPerson, ITravelerStepErrors } from "redmond";
import {
  Icon,
  IconName,
  LoyaltyInfoFormComponentProps,
  TravelerSelectDropdown,
  TravelerSelectStep,
  useCheckInMinimumAgeValidation,
  getAgeRequirementMessage,
} from "halifax";
import { HotelTravelerSelectDropdownFormConnectorProps } from "./container";
import { LoyaltyInformationForm } from "../LoyaltyInformationForm";
import {
  MULTIROOM_TOOLTIP_TEXT,
  MULTIROOM_TOOLTIP_TITLE,
  TRAVELER_SELECT_DROPDOWN_DESCRIPTION,
} from "./constants";
import "./styles.scss";

export interface HotelTravelerSelectDropdownFormProps
  extends HotelTravelerSelectDropdownFormConnectorProps {
  disableEditTravelerInfo: boolean;
  selectedTravelers: IPerson[];
  updateSelectedTravelersList: (traveler: IPerson) => void;
  removeSelectedTraveler: (traveler: IPerson) => void;
  setProgress: (progress: TravelerSelectStep) => void;
  showErrors: ITravelerStepErrors;
  setShowErrors: (showErrors: ITravelerStepErrors) => void;
  isLoyaltyEligible: boolean;
}

const getRenderLoyaltyComponent =
  (
    showErrors: ITravelerStepErrors,
    setShowErrors: (errors: ITravelerStepErrors) => void
  ) =>
  ({ onChange, disabled, traveler }: LoyaltyInfoFormComponentProps) =>
    (
      <LoyaltyInformationForm
        onChange={(newLoyaltyNumber) => onChange(newLoyaltyNumber)}
        disabled={disabled}
        traveler={traveler}
        setShowLoyaltyError={(isError) =>
          setShowErrors?.({
            ...showErrors,
            corpPrimaryTraveler: isError,
          })
        }
      />
    );

export const HotelTravelerSelectDropdownForm = ({
  roomsCount,
  travelers,
  disableEditTravelerInfo,
  selectedTravelers,
  updateSelectedTravelersList,
  updateUserPassenger,
  setProgress,
  removeSelectedTraveler,
  showErrors,
  setShowErrors,
  isLoyaltyEligible,
  chosenProduct,
  loyaltyProgram,
  userPassengerCallState,
  fetchCorpUserPassengers,
  minimumCheckInAge,
  checkInDate,
}: HotelTravelerSelectDropdownFormProps) => {
  const [dropdownCount, setDropdownCount] = useState(
    selectedTravelers.length || 1
  );

  const { disabledTravelerIds, sortedTravelers } =
    useCheckInMinimumAgeValidation({
      minimumAge: minimumCheckInAge,
      travelers,
      selectedTravelerId: selectedTravelers[0]?.id,
      checkInDate,
    });

  useEffect(() => {
    fetchCorpUserPassengers();
  }, []);

  useEffect(() => {
    if (disableEditTravelerInfo) {
      setDropdownCount(selectedTravelers.length);
    }
  }, [disableEditTravelerInfo]);

  const isAmadeusProvider = chosenProduct?.providerName === AMADEUS;

  const description = useMemo(() => {
    const baseDescription = TRAVELER_SELECT_DROPDOWN_DESCRIPTION(
      roomsCount,
      isAmadeusProvider,
      true,
    );

    if (minimumCheckInAge != null) {
      return `${baseDescription} ${getAgeRequirementMessage(
        minimumCheckInAge,
      )}`;
    }

    return baseDescription;
  }, [
    roomsCount,
    isAmadeusProvider,
    minimumCheckInAge,
  ]);

  return (
    <div className="hotels-traveler-select-dropdown-container">
      <div className="form-container">
        <Typography variant="h3" className="title">
          Select or add travelers
        </Typography>
        <Typography variant="body2" component="p" color="textPrimary">
          {description}
        </Typography>
        {[...Array(dropdownCount)].map((_, index) => (
          <Fragment key={index}>
            {index > 0 && <Divider style={{ margin: "20px 0" }} />}
            <TravelerSelectDropdown
              travelers={sortedTravelers}
              disableEditTravelerInfo={disableEditTravelerInfo}
              selectedTravelers={selectedTravelers}
              updateSelectedTraveler={updateSelectedTravelersList}
              removeSelectedTraveler={(traveler) => {
                removeSelectedTraveler(traveler);
                setDropdownCount((prevCount) =>
                  prevCount === 1 ? 1 : prevCount - 1
                );
              }}
              setProgress={setProgress}
              index={index}
              updateUserPassenger={(payload) =>
                updateUserPassenger(payload, false)
              }
              showErrors={showErrors}
              setShowErrors={setShowErrors}
              loyaltyProgram={loyaltyProgram}
              loading={userPassengerCallState === CallState.InProcess}
              renderLoyaltyComponent={
                isLoyaltyEligible
                  ? getRenderLoyaltyComponent(showErrors, setShowErrors)
                  : undefined
              }
              productType="hotel"
              disabledTravelerIds={disabledTravelerIds}
              minimumCheckInAge={minimumCheckInAge}
              checkInDate={checkInDate}
            />
          </Fragment>
        ))}
        {isAmadeusProvider && roomsCount > 1 && dropdownCount < roomsCount && (
          <Button
            variant="outlined"
            color="secondary"
            className="add-traveler-button"
            startIcon={<Add />}
            disabled={
              dropdownCount > Object.keys(selectedTravelers).length ||
              disableEditTravelerInfo
            }
            onClick={() => setDropdownCount((prevCount) => prevCount + 1)}
          >
            Add additional traveler
          </Button>
        )}
        {!isAmadeusProvider && roomsCount > 1 && (
          <Tooltip
            title={
              <Typography variant="subtitle2">
                {MULTIROOM_TOOLTIP_TEXT}
              </Typography>
            }
            placement="top"
            tabIndex={0}
          >
            <Box
              display="flex"
              className="multiroom-tooltip"
              alignItems="center"
              gridGap="6px"
            >
              <Typography variant="subtitle2">
                {MULTIROOM_TOOLTIP_TITLE}
              </Typography>
              <Icon name={IconName.InfoCircle} />
            </Box>
          </Tooltip>
        )}
      </div>
    </div>
  );
};
